import { FC, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Card from 'components/UI/Card';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Connectivity/styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ConnectivityDetails } from 'models';
import {
  connectivityDetailsSelector,
  fetchAnnotations,
} from 'store/connectivityState/connectivityState';
import Loader from 'components/UI/Loader';
import TableBody from 'components/UI/Table/TableBody';
import TableRow from 'components/UI/Table/TableRow';
import TableBodyCell from 'components/UI/Table/TableBodyCell';
import Table from 'components/UI/Table';
import TableHeader from 'components/UI/Table/TableHeader';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import InfoItem from 'components/UI/InfoItem';
import { HeaderCell } from 'components/UI/Table/SortableTable';
import { InfoGroup } from './styled';

type Props = {
  id: string;
};
const AnnotationsCard: FC<Props> = ({ id }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { annotations }: ConnectivityDetails = useAppSelector(
    connectivityDetailsSelector
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchAnnotations(id));
    }
  }, [dispatch, id]);

  const annotationInfoItemsResult = useMemo(() => {
    if (!annotations.data) {
      return null;
    }
    const { instanceId, adapter } = annotations.data;
    const detailList = [
      {
        id: 1,
        title: 'connectivity.annotations_adapter',
        text: adapter,
        conditional: false,
      },
      {
        id: 2,
        title: 'connectivity.annotations_instanceId',
        text: instanceId,
        conditional: false,
      },
    ];

    return detailList.map(({ id, title, text, conditional }) => {
      if (!text && conditional) {
        return null;
      }
      return (
        <InfoItem
          key={id}
          title={intl.formatMessage({ id: title })}
          value={text || '-'}
        />
      );
    });
  }, [annotations, intl]);

  const accessResult = useMemo(() => {
    if (annotations.isLoading) {
      return <Loader center />;
    }
    if (annotations.error) {
      return <Heading type={HeadingType.h4}>{annotations.error.title}</Heading>;
    }
    if (!annotations.data?.systems || annotations.data?.systems.length === 0) {
      return <Heading type={HeadingType.h4}>No accesses</Heading>;
    }

    const headerCells: HeaderCell[] = [
      {
        title: 'connectivity.annotations_system',
        sortable: false,
      },
      {
        title: 'connectivity.annotations_user',
        sortable: false,
      },
      {
        title: 'connectivity.annotations_access_level',
        sortable: false,
      },
    ];
    const bodyRows = annotations?.data?.systems.map(
      ({ system, user, accessLevel }) => {
        return {
          system,
          user,
          accessLevel,
          link: user,
        };
      }
    );
    return (
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              {headerCells.map((cell, index) => {
                return (
                  <TableHeaderCell key={index}>
                    {cell.title && <FormattedMessage id={cell.title} />}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {bodyRows.map((item, rowIndex) => {
              return (
                <TableRow key={rowIndex}>
                  {Object.entries(item).map(([name, value], colIndex) => (
                    <TableBodyCell key={colIndex} name={name} value={value} />
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  }, [annotations]);

  return (
    <div>
      <Card>
        <CardHeaderTop>
          <CardHeaderTopColumn>
            <Heading type={HeadingType.h3} uppercase>
              <FormattedMessage id="connectivity.annotations_registration" />
            </Heading>
          </CardHeaderTopColumn>
        </CardHeaderTop>
        <InfoGroup>{annotationInfoItemsResult}</InfoGroup>
      </Card>
      <Card>
        <CardHeaderTop>
          <CardHeaderTopColumn>
            <Heading type={HeadingType.h3} uppercase>
              <FormattedMessage id="connectivity.annotations_access" />
            </Heading>
          </CardHeaderTopColumn>
        </CardHeaderTop>
        {accessResult}
      </Card>
    </div>
  );
};

export default AnnotationsCard;
